import { AlignVAT, DifferentialPrice, DifferentialPriceForQuantity, DifferentialRangePrice, FromPrice, PriceDisplayMode, PriceInfo, RangePrice } from "@vp/ep-pricing";
import { Box, Typography } from "@vp/swan";
import { useUserContext } from "@vp/ubik-context";
import React from "react";
import { SHOW_ELEV8_WITH_TOTALPRICE_ON_CONSUMER } from "../../constants/featureFlags";
import { useFeatureToggle } from "../../hooks/useFeatureToggle";
import { LocalizationsProvider } from "../../hooks/useLocalizations";
import { getLocalizationsFor } from "../../localization/getLocalizations";
import { PriceDisplayType, PriceProperties, Prices } from "../../types/pricing/price-display.types";
import { PriceConfigBuilder } from "../../util/price-config";
import CSRWrapper from "../CSRWrapper";
import { MinimumOrderableQuantity } from "../MinimumOrderableQuantity";
import PriceErrorBoundary from "../PriceErrorBoundary";

export interface PriceDisplayWrapperProps {
  priceDisplayType: PriceDisplayType;
  prices: Prices;
  priceProperties: PriceProperties;
  placeholderText?: string;
}

const PriceDisplayWrapperCore = ({
  priceDisplayType,
  prices,
  priceProperties,
  placeholderText = "",
}: PriceDisplayWrapperProps) => {
  // Set default for price properties
  const normalizedPriceProperties: PriceProperties = {
    ...priceProperties,
    hideSetupFee: priceProperties.hideSetupFee ?? false,
    alignVAT: priceProperties.alignVAT ?? AlignVAT.BOTTOM,
    isTile: priceProperties.isTile ?? false,
  };
  const { locale } = useUserContext();

  const { isFeatureFlagEnabled } = useFeatureToggle();
  const showElev8WithTotalPriceOnConsumer = isFeatureFlagEnabled(SHOW_ELEV8_WITH_TOTALPRICE_ON_CONSUMER);

  if (!prices || !locale) return null;
  const builder = new PriceConfigBuilder(prices, normalizedPriceProperties, locale);
  const componentsByType: Record<PriceDisplayType, () => JSX.Element | null> = {
    [PriceDisplayType.PriceInfo]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.TOTAL);
      return config ? <PriceInfo placeholdertext={placeholderText} priceInfoConfig={config} doesOfferExist={priceProperties.doesOfferExist} alignVAT={normalizedPriceProperties.alignVAT} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.UnitPriceInfo]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.UNIT);
      return config ? <PriceInfo placeholdertext={placeholderText} priceInfoConfig={config} doesOfferExist={priceProperties.doesOfferExist} showTotalPriceForEachVariant={showElev8WithTotalPriceOnConsumer} alignVAT={normalizedPriceProperties.alignVAT} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.FromPrice]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.TOTAL);
      return config ? <FromPrice placeholdertext={placeholderText} fromPriceConfig={config} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.UnitFromPrice]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.UNIT);
      return config ? <FromPrice placeholdertext={placeholderText} fromPriceConfig={config} showTotalPriceForEachVariant={showElev8WithTotalPriceOnConsumer} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.DifferentialUnitRangePrice]: () => {
      const config = builder.getDifferentialRangePriceConfig();
      return config ? <DifferentialRangePrice placeholdertext={placeholderText} differentialRangePriceConfig={config} /> : null;
    },
    [PriceDisplayType.DifferentialPrice]: () => {
      const config = builder.getDifferentialConfig(PriceDisplayMode.TOTAL);
      return config ? <DifferentialPrice placeholdertext={placeholderText} differentialPriceConfig={config} /> : null;
    },
    [PriceDisplayType.UnitDifferentialPrice]: () => {
      const config = builder.getDifferentialConfig(PriceDisplayMode.UNIT);
      return config ? <DifferentialPrice placeholdertext={placeholderText} differentialPriceConfig={config} /> : null;
    },
    [PriceDisplayType.DifferentialPriceForQuantity]: () => {
      const config = builder.getDifferentialForQuantityConfig();
      return config ? <DifferentialPriceForQuantity placeholdertext={placeholderText} differentialPriceForQuantityConfig={config} /> : null;
    },
    [PriceDisplayType.UnitRangePrice]: () => {
      const config = builder.getRangePriceConfig(normalizedPriceProperties.isTile);
      return config ? (
        <>
          <RangePrice placeholdertext={placeholderText} rangePriceConfig={config} />
          {!priceProperties.hideMinimumOrderableQuantity && normalizedPriceProperties.quantity && (
            <Typography fontSize="small" textColor="subtle">
              <MinimumOrderableQuantity quantity={normalizedPriceProperties.quantity} alwaysShowMOQInfo={normalizedPriceProperties.alwaysShowMOQInfo} />
            </Typography>
          )}
        </>
      ) : null;
    },
  };

  return componentsByType[priceDisplayType]?.() ?? null;
};

const PriceDisplayWrapper = (priceDisplayWrapperProps: PriceDisplayWrapperProps) => {
  const { locale } = useUserContext();
  const localizations = getLocalizationsFor(locale);
  return (
    <CSRWrapper>
      <LocalizationsProvider localizations={localizations}>
        <PriceErrorBoundary fallbackUi={null}>
          <Box aria-live="polite" aria-atomic="true" data-testid="price-display-wrapper-test-id">
            <PriceDisplayWrapperCore {...priceDisplayWrapperProps} />
          </Box>
        </PriceErrorBoundary>
      </LocalizationsProvider>
    </CSRWrapper>
  );
};

export { PriceDisplayWrapper };
